import { state, updateState, resetSession, getUserRole } from '../state/store.js';
import { getBranding as getBrandingState } from '../state/branding.js';
import { privacyMarkdown, privacyHref } from '../utils/privacy.js';
import { sendChatMessage, sendFeedback, endSession } from '../api/client.js';
import { setMarkdown } from '../utils/markdown.js';

// DOM Elements
let chatMessages;
let chatInput;
let chatSendBtn;
let chatClearBtn;
let chatAudienceSelect;
let audienceBadge;
let currentAudience = 'end_user';

// Start texts end without a closing question — the greeting question is its
// own third bubble (after the optional privacy notice), see NOR-349.
export const WELCOME_MESSAGES = {
  end_user:
    '**Willkommen!**\nIch unterstütze Sie bei Fragen rund um die **SocialCard**.\n\n_Bitte geben Sie keine sensiblen persönlichen Daten (z.\u00a0B. Sozialversicherungsnummer, Bankdaten) in den Chat ein._',
  authority:
    '**Willkommen im Behörden-Modus!**\nIch unterstütze Sie bei Fragen zur **SocialCard-Administration** und behördenspezifischen Prozessen.\n\n_Bitte geben Sie keine sensiblen persönlichen Daten in den Chat ein._',
};

export const GREETING_QUESTION = 'Wie kann ich Ihnen heute helfen?';

const AUDIENCE_LABELS = {
  end_user: 'Endkunden',
  authority: 'Behörden',
};

// KB selector state: once branding delivers selectable_kbs, the dropdown holds
// KB names instead of audience values ("KB mode"). Without it (branding fetch
// failed / not yet loaded), the static audience options keep working.
let selectableKbs = [];
let kbMode = false;

function selectedKbEntry() {
  if (!kbMode || !chatAudienceSelect) return null;
  return selectableKbs.find((kb) => kb.name === chatAudienceSelect.value) || null;
}

// Called by main.js whenever branding (re-)loads.
export function applyKbOptions(kbs) {
  selectableKbs = Array.isArray(kbs) ? kbs : [];
  if (!chatAudienceSelect) return;
  const bar = chatAudienceSelect.closest('.chat-audience-bar');

  if (selectableKbs.length > 0) {
    kbMode = true;
    const previous = chatAudienceSelect.value;
    chatAudienceSelect.innerHTML = '';
    selectableKbs.forEach((kb) => {
      const option = document.createElement('option');
      option.value = kb.name;
      option.textContent = kb.label;
      chatAudienceSelect.appendChild(option);
    });
    const keep = selectableKbs.some((kb) => kb.name === previous)
      ? previous
      : selectableKbs[0].name;
    chatAudienceSelect.value = keep;
    currentAudience = keep;
    if (bar) bar.style.display = selectableKbs.length > 1 ? '' : 'none';
  } else {
    // Anonymous visitors are pinned to the end-user KB server-side.
    kbMode = false;
    if (bar) bar.style.display = 'none';
  }
  updateAudienceBadge();
}

export function initChat() {
  // Get DOM elements
  chatMessages = document.getElementById('chatMessages');
  chatInput = document.getElementById('chatInput');
  chatSendBtn = document.getElementById('chatSendBtn');
  chatClearBtn = document.getElementById('chatClearBtn');
  chatAudienceSelect = document.getElementById('chatAudience');
  audienceBadge = document.getElementById('chatAudienceBadge');

  // Send message on button click
  if (chatSendBtn) {
    chatSendBtn.addEventListener('click', handleSendMessage);
  }

  // Send message on Enter key (Shift+Enter for new line)
  if (chatInput) {
    chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    });
    chatInput.addEventListener('input', autoResizeChatInput);
    autoResizeChatInput();
  }

  // Clear chat history
  if (chatClearBtn) {
    chatClearBtn.addEventListener('click', handleClearChat);
  }

  // Audience switch handler — warn if conversation exists
  if (chatAudienceSelect) {
    currentAudience = chatAudienceSelect.value || 'end_user';
    chatAudienceSelect.value = currentAudience;
    chatAudienceSelect.addEventListener('change', handleAudienceChange);
  }

  // Track session end when the page is actually being left.
  const closeSession = () => {
    if (state.sessionId && state.chatHistory.length > 0) {
      endSession(state.sessionId);
    }
  };
  window.addEventListener('beforeunload', closeSession);
  window.addEventListener('pagehide', closeSession);

  // Kommunen accounts are locked to the authority audience — apply immediately and
  // again after a fresh login (role isn't known yet on first paint if login is pending).
  applyAudienceAccessForRole();
  window.addEventListener('auth-success', applyAudienceAccessForRole);

  // Initialize with welcome message and badge
  updateAudienceBadge();
  displayWelcomeMessage();
}

function applyAudienceAccessForRole() {
  // In KB mode the selector is already restricted to the role's allowed KBs.
  if (kbMode) return;
  const isKommunen = getUserRole() === 'kommunen';
  const audienceBar = chatAudienceSelect ? chatAudienceSelect.closest('.chat-audience-bar') : null;
  if (audienceBar) {
    audienceBar.style.display = isKommunen ? 'none' : '';
  }
  if (isKommunen && chatAudienceSelect) {
    chatAudienceSelect.value = 'authority';
    currentAudience = 'authority';
    updateAudienceBadge();
  }
}

function getSelectedAudience() {
  // Audience key for welcome messages / badge coloring. In KB mode it derives
  // from the selected KB (custom KBs count as end_user for wording purposes).
  if (kbMode) {
    const entry = selectedKbEntry();
    return (entry && entry.audience) || 'end_user';
  }
  return chatAudienceSelect ? chatAudienceSelect.value : currentAudience;
}

function selectedLabel() {
  if (kbMode) {
    const entry = selectedKbEntry();
    return entry ? entry.label : '';
  }
  const audience = getSelectedAudience();
  return AUDIENCE_LABELS[audience] || audience;
}

function updateAudienceBadge() {
  if (audienceBadge) {
    audienceBadge.textContent = selectedLabel();
    audienceBadge.className = 'chat-audience-badge chat-audience-badge--' + getSelectedAudience();
  }
  // The footer privacy link tracks the KB selection alongside the badge.
  updatePrivacyLink();
}

function handleAudienceChange() {
  if (!chatAudienceSelect) return;
  const newAudience = chatAudienceSelect.value;
  if (newAudience === currentAudience) return;

  // Avoid switching audience while a streamed response is in flight.
  if (chatSendBtn && chatSendBtn.disabled) {
    chatAudienceSelect.value = currentAudience;
    return;
  }

  // If there's an active conversation, warn before switching
  if (state.chatHistory.length > 0) {
    const label = selectedLabel() || newAudience;
    const confirmed = confirm(
      `Wissensdatenbank wechseln zu "${label}"?\n\nDer aktuelle Chatverlauf wird gelöscht, da die Wissensbasis wechselt.`
    );
    if (!confirmed) {
      // Revert dropdown to previous value
      chatAudienceSelect.value = currentAudience;
      return;
    }
  }

  // End current session
  if (state.sessionId && state.chatHistory.length > 0) {
    endSession(state.sessionId);
  }

  // Clear and reset
  if (chatMessages) chatMessages.innerHTML = '';
  updateState({ chatHistory: [], selectedDocuments: [] });
  resetSession();
  currentAudience = newAudience;
  if (chatInput) {
    chatInput.value = '';
    autoResizeChatInput();
  }

  // Update badge and show audience-appropriate welcome
  updateAudienceBadge();
  displayWelcomeMessage();
}

// welcome_text / privacy_url for the current selection: the selected KB's
// group-resolved values first, the viewer's own branding as fallback.
function effectiveKbBranding() {
  const branding = getBrandingState();
  const entry = selectedKbEntry();
  return {
    welcomeText: (entry && entry.welcome_text) || branding?.welcome_text || '',
    privacyUrl: (entry && entry.privacy_url) || branding?.privacy_url || '',
  };
}

function updatePrivacyLink() {
  const privacyLink = document.getElementById('privacyLink');
  if (!privacyLink) return;
  const href = privacyHref(effectiveKbBranding().privacyUrl);
  if (href) {
    privacyLink.href = href;
    privacyLink.style.display = '';
  } else {
    privacyLink.style.display = 'none';
  }
}

// Conversation opener: start text, then the privacy notice as its own bubble
// (when configured), then the greeting question (NOR-349).
function displayWelcomeMessage() {
  if (!chatMessages) return;

  const { welcomeText, privacyUrl } = effectiveKbBranding();
  const audience = getSelectedAudience();
  const text = welcomeText || WELCOME_MESSAGES[audience] || WELCOME_MESSAGES.end_user;
  chatMessages.appendChild(createMessageElement('assistant', text));

  const privacyNotice = privacyMarkdown(privacyUrl);
  if (privacyNotice) {
    chatMessages.appendChild(createMessageElement('assistant', privacyNotice));
  }

  chatMessages.appendChild(createMessageElement('assistant', GREETING_QUESTION));
}

// Re-render the welcome message (e.g. after branding loaded), but only while
// the conversation hasn't started yet.
export function refreshWelcomeMessage() {
  if (!chatMessages) return;
  if (chatMessages.querySelector('.user-message')) return;
  chatMessages.innerHTML = '';
  displayWelcomeMessage();
}

function createMessageElement(role, content) {
  const messageDiv = document.createElement('div');
  messageDiv.className = `chat-message ${role}-message`;

  const avatar = document.createElement('div');
  avatar.className = 'message-avatar';
  if (role === 'assistant') {
    const img = document.createElement('img');
    img.src = '/publk_circle_purple.png';
    img.alt = 'Assistent';
    avatar.appendChild(img);
  } else {
    // Insert a simple human silhouette SVG for user avatar
    avatar.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5zm0 2c-4.418 0-8 2.238-8 5v1h16v-1c0-2.762-3.582-5-8-5z" />
      </svg>
    `;
  }

  const contentDiv = document.createElement('div');
  contentDiv.className = 'message-content';

  // Render markdown for assistant messages, plain text for user messages
  if (role === 'assistant') {
    // Sanitized: content originates from uploaded documents / LLM output.
    setMarkdown(contentDiv, content);
  } else {
    contentDiv.textContent = content;
  }

  messageDiv.appendChild(avatar);
  messageDiv.appendChild(contentDiv);

  return messageDiv;
}

function createLoadingElement() {
  const messageDiv = document.createElement('div');
  messageDiv.className = 'chat-message assistant-message loading-message';

  const avatar = document.createElement('div');
  avatar.className = 'message-avatar';
  const img = document.createElement('img');
  const logoPath = encodeURI('/publk_circle_purple.png');
  img.src = logoPath;
  img.alt = 'Assistent';
  img.onerror = () => {
    img.onerror = null;
    img.src = '/socialcard_logo_rgb_unterzeile_72dpi.png';
  };
  avatar.appendChild(img);

  const contentDiv = document.createElement('div');
  contentDiv.className = 'message-content';

  const loadingDots = document.createElement('div');
  loadingDots.className = 'loading-dots';
  loadingDots.innerHTML = '<span></span><span></span><span></span>';

  contentDiv.appendChild(loadingDots);
  messageDiv.appendChild(avatar);
  messageDiv.appendChild(contentDiv);

  return messageDiv;
}

function createFeedbackButtons(messageId) {
  const bar = document.createElement('div');
  bar.className = 'feedback-bar';

  const label = document.createElement('span');
  label.className = 'feedback-label';
  label.textContent = 'War diese Antwort hilfreich?';

  const thumbsUp = document.createElement('button');
  thumbsUp.className = 'feedback-btn feedback-positive';
  thumbsUp.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
  </svg>`;
  thumbsUp.title = 'Hilfreich';

  const thumbsDown = document.createElement('button');
  thumbsDown.className = 'feedback-btn feedback-negative';
  thumbsDown.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
  </svg>`;
  thumbsDown.title = 'Nicht hilfreich';

  const handleFeedback = async (rating, clickedBtn, otherBtn) => {
    try {
      await sendFeedback(messageId, state.sessionId, rating);
      clickedBtn.classList.add('active');
      clickedBtn.disabled = true;
      otherBtn.disabled = true;
      otherBtn.classList.add('disabled');
      label.textContent = rating === 'positive' ? 'Danke für Ihr Feedback!' : 'Danke, wir arbeiten daran!';
    } catch (error) {
      console.error('Feedback error:', error);
    }
  };

  thumbsUp.addEventListener('click', () => handleFeedback('positive', thumbsUp, thumbsDown));
  thumbsDown.addEventListener('click', () => handleFeedback('negative', thumbsDown, thumbsUp));

  bar.appendChild(label);
  bar.appendChild(thumbsUp);
  bar.appendChild(thumbsDown);

  return bar;
}

async function handleSendMessage() {
  const message = chatInput.value.trim();

  if (!message) return;

  // Add user message to chat
  const userMessage = createMessageElement('user', message);
  chatMessages.appendChild(userMessage);

  // Clear input
  chatInput.value = '';
  autoResizeChatInput();

  // Scroll to bottom
  scrollToBottom();

  // Create assistant message container for streaming
  const assistantMessageDiv = document.createElement('div');
  assistantMessageDiv.className = 'chat-message assistant-message';

  const avatar = document.createElement('div');
  avatar.className = 'message-avatar';
  const img = document.createElement('img');
  const logoPath = encodeURI('/publk_circle_purple.png');
  img.src = logoPath;
  img.alt = 'SocialCard';
  img.onerror = () => {
    img.onerror = null;
    img.src = '/socialcard_logo_rgb_unterzeile_72dpi.png';
  };
  avatar.appendChild(img);

  const contentDiv = document.createElement('div');
  contentDiv.className = 'message-content streaming';

  assistantMessageDiv.appendChild(avatar);
  assistantMessageDiv.appendChild(contentDiv);
  chatMessages.appendChild(assistantMessageDiv);
  scrollToBottom();

  // Disable input while processing
  chatInput.disabled = true;
  chatSendBtn.disabled = true;
  if (chatClearBtn) chatClearBtn.disabled = true;
  if (chatAudienceSelect) chatAudienceSelect.disabled = true;

  let fullAnswer = '';

  try {
    // Send message with streaming callback, document filter, session ID, and
    // target: the selected KB in KB mode, the audience value otherwise.
    const collection = kbMode && chatAudienceSelect ? chatAudienceSelect.value : null;
    const audience = !kbMode && chatAudienceSelect ? chatAudienceSelect.value : null;
    const result = await sendChatMessage(message, state.chatHistory, (chunk) => {
      fullAnswer += chunk;
      // Update the content with markdown rendering
      setMarkdown(contentDiv, fullAnswer);
      scrollToBottom();
    }, state.selectedDocuments, state.sessionId, audience, collection);

    // Remove streaming class when done
    contentDiv.classList.remove('streaming');

    // Add feedback buttons after bot response
    if (result && result.messageId) {
      const feedbackBar = createFeedbackButtons(result.messageId);
      assistantMessageDiv.appendChild(feedbackBar);
      scrollToBottom();
    }

    // Update chat history
    const newHistory = [
      ...state.chatHistory,
      { role: 'user', content: message },
      { role: 'assistant', content: fullAnswer }
    ];
    updateState({ chatHistory: newHistory });

  } catch (error) {
    console.error('Error sending message:', error);

    // Remove the streaming message
    if (assistantMessageDiv.parentNode === chatMessages) {
      chatMessages.removeChild(assistantMessageDiv);
    }

    // Show the backend's error detail when we have one — a bare generic
    // sentence made real deployment issues undiagnosable from user reports.
    // Network-level failures (TypeError) keep the generic wording.
    const detail =
      error instanceof TypeError ? '' : (error && error.message) || '';
    const errorMessage = createMessageElement(
      'assistant',
      detail
        ? `Es ist ein Fehler aufgetreten: ${detail}`
        : 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.'
    );
    chatMessages.appendChild(errorMessage);
    scrollToBottom();
  } finally {
    // Re-enable input
    chatInput.disabled = false;
    chatSendBtn.disabled = false;
    if (chatClearBtn) chatClearBtn.disabled = false;
    if (chatAudienceSelect) chatAudienceSelect.disabled = false;
    chatInput.focus();
  }
}

function handleClearChat() {
  if (!chatMessages) return;

  // End current session before clearing
  if (state.sessionId && state.chatHistory.length > 0) {
    endSession(state.sessionId);
  }

  // Clear all messages
  chatMessages.innerHTML = '';

  // Reset chat history and start new session
  updateState({ chatHistory: [] });
  resetSession();

  // Show welcome message again
  displayWelcomeMessage();
}

function scrollToBottom() {
  if (chatMessages) {
    chatMessages.scrollTop = chatMessages.scrollHeight;
  }
}

function autoResizeChatInput() {
  if (!chatInput) return;

  const maxHeight = 150;
  chatInput.style.height = 'auto';
  chatInput.style.height = `${Math.min(chatInput.scrollHeight, maxHeight)}px`;
  chatInput.style.overflowY = chatInput.scrollHeight > maxHeight ? 'auto' : 'hidden';
}
